import { createElement, type ComponentType, type MouseEvent } from "react";
import type { DataKind } from "../access/dataKinds";
import type {
    FilesCapability,
    MenusCapability,
    OfflineCapability,
    Platform,
    WindowsCapability,
} from "../platform/Platform";
import { localDeviceLabel } from "../shell/shortcuts";
import type { SidebarItem, SidebarSection } from "../shell/types";

/**
 * Every screen key. Runtime-readable so a host can validate a persisted route key
 * (`isScreenKey`) without a second hand-kept list.
 */
export const SCREEN_KEYS = [
    "overview",
    "questions",
    "drawings",
    "specifications",
    "documents",
    "models",
    "documentsContracts",
    "documentsCode",
    "documentsIfoa",
    "documentsPrimeContract",
    "documentsSupplemental",
    "documentsAll",
    "documentsTransmittals",
    "documentsAccFiles",
    "documentsProcoreFiles",
    "documentsSharepointFiles",
    // An item's attachments in the file viewer: reached by Create, never from the sidebar.
    "attachmentEditor",
    "directory",
    "schedule",
    "toolControlTower",
    "toolConstructability",
    "toolClashReview",
    "toolRfi",
    "toolSubmittalReview",
    "toolSubmittalRegister",
    "toolRequirementsCheck",
    "toolShopReview",
    "toolRevision",
    "toolTimeline",
    "toolFileComparison",
    "toolQualityDashboard",
    "toolDeepSearch",
    "toolWorkbench",
    "toolMcp",
    "toolWorkflowStudio",
    "portfolio",
    "settings",
    "offline",
    "thisMachine",
    "favorites",
    // Project Management (`features.project_management.enabled`): one screen per module
    // of `shared/documentModules.ts`, plus Project settings, registered by
    // `screens/ProjectManagement/register.ts`.
    "pmOverview",
    "pmMyWork",
    "pmRfis",
    "pmSubmittals",
    "pmTransmittals",
    "pmPunch",
    "pmIssues",
    "pmInspectionSummary",
    "pmInspections",
    "pmDdnc",
    "pmInspectionCharges",
    "pmDailyReports",
    "pmMeetings",
    "pmForms",
    "pmProvisions",
    "pmPhotos",
    "pmReports",
    "pmProjectSettings",
] as const;

export type ScreenKey = (typeof SCREEN_KEYS)[number];

export function isScreenKey(value: unknown): value is ScreenKey {
    return typeof value === "string" && (SCREEN_KEYS as readonly string[]).includes(value);
}

/** The optional `Platform` capabilities a screen can require. `offline` is never
 *  required bare: a screen names the sub-capability it calls. */
type PlatformCapabilityName = "files" | "windows" | "menus";

/**
 * One requirement a screen declares: a bare capability (`"files"`), one member of
 * it (`"files.listFavorites"`), or one offline sub-capability (`"offline.sync"`).
 * Members are typed off the capability interfaces, so a renamed method fails to
 * compile here. There is no predicate form: a requirement names what the screen
 * calls, never which host runs it.
 */
export type ScreenCapability =
    | PlatformCapabilityName
    | `offline.${Extract<keyof OfflineCapability, string>}`
    | `files.${Extract<keyof FilesCapability, string>}`
    | `windows.${Extract<keyof WindowsCapability, string>}`
    | `menus.${Extract<keyof MenusCapability, string>}`;

/**
 * A tenant gate a screen needs before the sidebar offers it (the web's Tools list
 * applies the same gates). The shell evaluates the gates; the registry only names them.
 */
export type ScreenGate =
    | "controlTower"
    | "clashReview"
    | "requirementsCheck"
    | "shopReview"
    | "fileComparison"
    | "workbench"
    | "mcp"
    /** `features.quality_dashboard.enabled`, resolved and on. */
    | "qualityDashboard"
    /** Tool Builder: `features.workflow_studio.enabled` on and the viewer a Stytch admin, the
     *  rule its route and the server apply to building a tool. */
    | "workflowStudio"
    /** `features.timeline.enabled`, resolved and on: the Timeline, with Project Management on or off. */
    | "timeline"
    /** `features.models_viewer.enabled`, resolved and on: Documents → Models and its viewer. */
    | "models"
    /** `features.project_management.enabled` (the workspace), resolved and on. Never true while it loads. */
    | "projectManagement"
    /** The workspace and `features.primepoint_records.enabled` (Primepoint as the system of
     *  record), both resolved and on: a native module's screen. */
    | "primepointRecords"
    /** The same flag resolved off: the document-control transmittal workspace stands in for
     *  the Transmittals module. Never true while it loads. */
    | "legacyTransmittals"
    /** Admin: offered when the viewer can use one of its sections by a capability the server
     *  declares (manage access, access activity, Procore import, project settings). Project
     *  Management being on is not enough. Navigation only; each section enforces its own. */
    | "projectSettings"
    /** A Field screen: its module's requirement met (`moduleRequirementMet`: the system of
     *  record inside the workspace, or for punch and issues the workspace with a provider that
     *  takes the kind) and the viewer's tool permissions (`/me` `effective_tool_permissions`)
     *  include its tool. */
    | "fieldDailyReports"
    | "fieldPunch"
    | "fieldIssues"
    | "fieldForms"
    | "inspections"
    | "inspectionCharges"
    /** Deep search: on unless the viewer's tool policy lists Search with no action (catalog v5
     *  has no Search tool). */
    | "search"
    /** The Directory: Project Management on, the project's access foundation on (/me
     *  `protected_downloads`), or the one Directory on (`features.one_directory`) with the
     *  Directory tool. Never true while the flag loads. */
    | "directory";

/** A gate per kind of data: `held.<kind>` is false when the viewer holds that kind at None. */
export type DataKindGate = `held.${DataKind}`;

export const dataKindGate = (kind: DataKind): DataKindGate => `held.${kind}`;

/** The evaluated tenant gates a host hands the registry (`useToolGates`). */
export type ScreenGates = Readonly<Partial<Record<ScreenGate | DataKindGate, boolean>>>;

export type SidebarSectionKey = "project" | "projectManagement" | "tools" | "local" | "settings";

/** A web Material glyph. The sidebar renders it at 16 px through `fontSize="inherit"`. */
export type ScreenIcon = ComponentType<{ fontSize?: "inherit" }>;

export interface ScreenSidebarPlacement {
    section: SidebarSectionKey;
    label: string;
    icon: ScreenIcon;
    /** Nests this row under another screen's row (Schedule under Documents). */
    parentKey?: ScreenKey;
    /**
     * Position inside the section, ascending. The web IA: overview 10, (Project
     * Management's My work 15), timeline 16, questions 20, drawings 30, specifications 40,
     * documents 50 (categories 51..59), rfis 60, submittals 70; tools: the web's Tools order;
     * local: thisMachine 10, favorites 20, offline 30.
     */
    order: number;
}

export interface ScreenDefinition {
    key: ScreenKey;
    /** Route path inside a tab (desktop MemoryRouter) and on the web BrowserRouter. */
    path: string;
    /**
     * Where the classic web (Design v2 off) shows this screen's content, for a screen whose
     * `path` the classic route tree does not serve itself. The classic tree sends `path` there,
     * so a link built from `path` (`urlForTarget`), a bookmark, or an address a Design v2 user
     * shared lands on a page classic has. Omit it when the classic tree serves `path` itself
     * (Home, Drawings, the Documents categories, the tools).
     */
    classicPath?: string;
    /** The screen's display name where a host needs one and there is no sidebar row to
     *  take it from (a desktop tab title). `screenTitle` reads it. */
    title?: string;
    Component: ComponentType;
    /** Each requirement names a capability the screen calls. It never names a host. */
    requires?: ReadonlyArray<ScreenCapability>;
    /** A tenant gate the sidebar checks through `SidebarBinding.gates`. */
    gate?: ScreenGate;
    /**
     * The kind of data the screen lists (a `/me` `effective_tool_permissions` key). The sidebar
     * leaves the screen out while the viewer holds that kind at None (`held.<kind>` false).
     */
    dataKind?: DataKind;
    /** Sidebar placement; omit for screens reached only by navigation. */
    sidebar?: ScreenSidebarPlacement;
    /**
     * Whether a host must confirm a signed-in tenant session before it mounts this
     * screen's `Component`. A cloud screen mounted while signed out fires its data
     * request and fails with a raw 401 (Build 34 Windows smoke finding, 2026-09-03);
     * `screenNeedsTenant` below is the one place a host reads this. Optional: defaults
     * to true for a `project`/`tools` sidebar section, false otherwise. Set explicitly
     * to override the default (Home is a `project`-section screen that never needs a
     * tenant).
     */
    needsTenant?: boolean;
}

/** The name a host shows for `screen`: its own `title`, else its sidebar row's label,
 *  else the key (a name that never renders as prose). */
export function screenTitle(screen: ScreenDefinition): string {
    return screen.title ?? screen.sidebar?.label ?? screen.key;
}

/** Whether `screen` needs a signed-in tenant session before a host mounts it. See
 *  `ScreenDefinition.needsTenant`. */
export function screenNeedsTenant(screen: ScreenDefinition): boolean {
    if (screen.needsTenant !== undefined) return screen.needsTenant;
    const section = screen.sidebar?.section;
    return section === "project" || section === "projectManagement" || section === "tools";
}

/**
 * Every registered screen. Each screen lane pushes its entries from its own
 * `client/src/screens/<Name>/register.ts`; `client/src/screens/index.ts` imports them.
 */
export const SCREENS: ScreenDefinition[] = [];

export function screenByPath(
    screens: readonly ScreenDefinition[],
    path: string,
): ScreenDefinition | undefined {
    return screens.find((screen) => screen.path === path);
}

export function screenByKey(
    screens: readonly ScreenDefinition[],
    key: ScreenKey,
): ScreenDefinition | undefined {
    return screens.find((screen) => screen.key === key);
}

/** Resolves one requirement: the capability must exist, and so must the named member
 *  (`"offline.sync"` means `platform.offline?.sync !== undefined`). */
function hasScreenCapability(platform: Platform, requirement: ScreenCapability): boolean {
    const [name, member] = requirement.split(".") as [
        PlatformCapabilityName | "offline",
        string | undefined,
    ];
    const capability = platform[name];
    if (capability === undefined) return false;
    return member === undefined || Reflect.get(capability, member) !== undefined;
}

/** True when every capability the screen requires exists on the platform. */
export function isScreenAvailable(screen: ScreenDefinition, platform: Platform): boolean {
    return (screen.requires ?? []).every((requirement) =>
        hasScreenCapability(platform, requirement),
    );
}

export interface SidebarBinding {
    /** Receives the click so the host can honor Cmd/Ctrl-click and middle-click. */
    onSelect(screen: ScreenDefinition, event: MouseEvent<HTMLButtonElement>): void;
    /** Live counts keyed by screen. A missing key renders no count. */
    counts?: Partial<Record<ScreenKey, number | null>>;
    /** Evaluated tenant gates. A gated screen is offered only when its gate is true. */
    gates?: ScreenGates;
}

/**
 * Whether the tenant gates offer `screen`: its gate is open, and its kind of data is not held
 * at None. A kind with no answer yet offers the screen; a gate with no answer does not.
 */
export function screenGatesOpen(screen: ScreenDefinition, gates: ScreenGates | undefined): boolean {
    return (
        (screen.gate === undefined || gates?.[screen.gate] === true) &&
        (screen.dataKind === undefined || gates?.[dataKindGate(screen.dataKind)] !== false)
    );
}

export type PlacedScreen = ScreenDefinition & { sidebar: ScreenSidebarPlacement };

function hasSidebar(screen: ScreenDefinition): screen is PlacedScreen {
    return screen.sidebar !== undefined;
}

/** The screens with a sidebar placement that the platform can offer and the tenant gates allow,
 *  in sidebar order: the one rule behind every navigation list (the sidebar, the rail, the
 *  classic header's Tools menu). */
export function offeredScreens(
    screens: readonly ScreenDefinition[],
    platform: Platform,
    gates: SidebarBinding["gates"],
): PlacedScreen[] {
    return screens
        .filter(hasSidebar)
        .filter((screen) => isScreenAvailable(screen, platform) && screenGatesOpen(screen, gates))
        .sort((a, b) => a.sidebar.order - b.sidebar.order);
}

/** One sidebar row for a screen that has a sidebar placement. */
export function sidebarItemFrom(
    screen: PlacedScreen,
    platform: Platform,
    binding: SidebarBinding,
    children?: readonly SidebarItem[],
): SidebarItem {
    const count = binding.counts?.[screen.key];
    return {
        key: screen.key,
        // "This Mac" beside a ⌘, "This PC" beside a Ctrl: the host decides, not the registrar.
        label:
            screen.key === "thisMachine"
                ? localDeviceLabel(platform.shortcuts.modifier)
                : screen.sidebar.label,
        icon: createElement(screen.sidebar.icon, { fontSize: "inherit" }),
        ...(count === undefined ? {} : { count }),
        onSelect: (event) => binding.onSelect(screen, event),
        ...(children && children.length > 0 ? { children } : {}),
    };
}

const SECTION_ORDER = ["project", "projectManagement", "tools", "local"] as const;

const SECTION_LABELS: Readonly<Record<(typeof SECTION_ORDER)[number], string | undefined>> = {
    project: undefined,
    projectManagement: "Project management",
    tools: "Tools",
    local: "Local",
};

/**
 * The `project`, `projectManagement`, `tools` and `local` sections for the shell `Sidebar`, in web IA order,
 * with only the screens the platform can offer and the tenant gates allow. A screen with
 * `parentKey` nests under its parent; when the parent is absent it keeps its own row so
 * nothing disappears. The `settings` section is not here: Settings opens from the account
 * menu.
 */
export function sidebarSectionsFrom(
    screens: readonly ScreenDefinition[],
    platform: Platform,
    binding: SidebarBinding,
): SidebarSection[] {
    const offered = offeredScreens(screens, platform, binding.gates);
    const offeredKeys = new Set(offered.map((screen) => screen.key));
    const sections: SidebarSection[] = [];
    for (const section of SECTION_ORDER) {
        const items = offered
            .filter(
                (screen) =>
                    screen.sidebar.section === section &&
                    (screen.sidebar.parentKey === undefined ||
                        !offeredKeys.has(screen.sidebar.parentKey)),
            )
            .map((root) => {
                const children = offered
                    .filter((screen) => screen.sidebar.parentKey === root.key)
                    .map((child) => sidebarItemFrom(child, platform, binding));
                return sidebarItemFrom(root, platform, binding, children);
            });
        if (items.length === 0) continue;
        const label = SECTION_LABELS[section];
        sections.push({
            key: section,
            ...(label ? { label } : {}),
            items,
        });
    }
    return sections;
}
